import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FieldPath, FieldValues, Path, PathValue, get, useFormState, useWatch } from 'react-hook-form';
import { FormFieldProps, Nullable, TextInputField } from '@brighte/form-components';
import { requiredFieldError } from '@brighte/validation';
import { Occupation, useGetOccupationMatchQuery } from '@graphql/index';
import { Alert } from '@spark-web/alert';
import { Combobox } from '@spark-web/combobox';
import { Field } from '@spark-web/field';
import { InformationCircleIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { LABELS, ManualOccupationJobTitleMatchingType } from '../../utils';

const MANUAL_OCCUPATION_CODE = '0'; //defined by backend
type OptionType = {
  label: string;
  value: string;
};

type DefaultOptionType = {
  option: {
    label: string;
    value: string;
  };
  position?: 'start' | 'end' | undefined;
};

const defaultManualOccupation: DefaultOptionType = {
  option: {
    label: 'Other',
    value: MANUAL_OCCUPATION_CODE,
  },
  position: 'end',
};

type Props<TFieldValues extends FieldValues> = {
  /**
   * Label for Occupation search input
   * @default 'Occupation'
   */
  label?: string;
  onChange?: (value: string | null) => void;
  setOccupationIncomeThreshold: Dispatch<SetStateAction<number>>;
  occupations: Occupation[];
  isOccupationLoading: boolean;
  manualOccupationJobTitle: ManualOccupationJobTitleMatchingType;
  setManualOccupationJobTitle?: Dispatch<SetStateAction<ManualOccupationJobTitleMatchingType>>;
} & FormFieldProps<TFieldValues>;

function OccupationField<TFieldValues extends FieldValues = FieldValues>({
  label = 'Occupation',
  name,
  control,
  setValue,
  register,
  clearErrors,
  onChange,
  setOccupationIncomeThreshold,
  occupations,
  isOccupationLoading,
  manualOccupationJobTitle,
  setManualOccupationJobTitle,
}: Props<TFieldValues>) {
  const { MANUAL_OCCUPATION, OCCUPATION_DESCRIPTION } = LABELS;
  const jobTitleFieldName = `${name}.jobTitle` as FieldPath<TFieldValues>;
  const codeFieldName = `${name}.code` as FieldPath<TFieldValues>;
  const [applicantOccupationCode, applicantOccupationJobTitle] = useWatch({
    control,
    name: [codeFieldName, jobTitleFieldName],
  });

  const initialJobTitle =
    applicantOccupationCode === MANUAL_OCCUPATION_CODE
      ? defaultManualOccupation.option.label
      : applicantOccupationJobTitle;

  const otherOption = {
    label: defaultManualOccupation.option.label ?? '',
    value: defaultManualOccupation.option.value ?? '',
  };
  const [inputValue, setInputValue] = useState('');
  const [occupationState, setOccupationState] = useState<{ occupation: OptionType | null; showManual: boolean }>({
    occupation: applicantOccupationCode
      ? {
          label: initialJobTitle,
          value: applicantOccupationCode,
        }
      : null,
    showManual: applicantOccupationCode === MANUAL_OCCUPATION_CODE ? true : false,
  });

  const {
    data: occupationMatchData,
    isFetching: occupationMatchFetching,
    isError, // just in case request timeouts
  } = useGetOccupationMatchQuery(
    { input: { jobTitle: manualOccupationJobTitle.jobTitle } },
    {
      enabled: manualOccupationJobTitle.jobTitle !== '' && manualOccupationJobTitle.isMatching, //only trigger if jobtitle is not empty and onBlur is triggered
      select: data => {
        const match = data.occupationMatch;
        return match?.matches?.confidenceLevel.toLowerCase() === 'high' ? match : null;
      },
      retry: 2, // only retry twice, incase of request time out
    },
  );

  useEffect(() => {
    // This is needed to set the value of jobTitle as it is a hidden input if showManual is false
    if (!occupationState.showManual && occupationState.occupation?.label) {
      setValue?.(jobTitleFieldName, occupationState.occupation?.label as PathValue<TFieldValues, Path<TFieldValues>>);
    }
    if (occupationState.showManual && (!occupationMatchFetching || isError) && manualOccupationJobTitle.isMatching) {
      setValue?.(jobTitleFieldName, manualOccupationJobTitle.jobTitle as PathValue<TFieldValues, Path<TFieldValues>>);
      setManualOccupationJobTitle?.(currValue => ({
        ...currValue,
        isMatched: true,
        isMatching: false,
        isFetched: true,
      }));
      const matchedData = occupationMatchData
        ? { code: occupationMatchData?.matches?.code?.toString(), salary: occupationMatchData?.matches?.salary ?? 0 }
        : { code: '0', salary: 0 };
      setValue?.(codeFieldName, matchedData.code as PathValue<TFieldValues, Path<TFieldValues>>);
      setOccupationIncomeThreshold(matchedData.salary);
    }
  }, [occupationMatchData, occupationState, occupationMatchFetching, isError]);

  const { errors } = useFormState({ control });
  const showFieldError =
    (!!get(errors, jobTitleFieldName) || !!get(errors, codeFieldName)) && !occupationState.showManual;
  const handleInputChange = (value: string) => setInputValue(value);

  const handleChange = (newValue: Nullable<OptionType>) => {
    const newOption = { label: newValue?.label ?? '', value: newValue?.value ?? '' };
    setValue?.(codeFieldName, newValue?.value as PathValue<TFieldValues, Path<TFieldValues>>);
    clearErrors?.(codeFieldName);
    clearErrors?.(jobTitleFieldName);
    if (newValue?.value === MANUAL_OCCUPATION_CODE) {
      setOccupationState({ occupation: otherOption, showManual: true });
      setValue?.(jobTitleFieldName, inputValue as PathValue<TFieldValues, Path<TFieldValues>>);
      setManualOccupationJobTitle?.({ jobTitle: inputValue, isMatching: true, isFetched: false, isMatched: false });
    } else {
      const selectedOccupation = occupations && occupations.find(occupation => occupation.code === newValue?.value);
      setValue?.(jobTitleFieldName, '' as PathValue<TFieldValues, Path<TFieldValues>>);
      setManualOccupationJobTitle?.({ jobTitle: '', isMatching: false, isFetched: false, isMatched: true });
      setOccupationState({ occupation: newOption ?? '', showManual: false });
      setOccupationIncomeThreshold(selectedOccupation?.incomeThreshold ?? 0);
    }
    onChange?.(newValue?.value as string | null);
  };

  const items: OptionType[] =
    occupations
      ?.filter(occupation => occupation.code !== MANUAL_OCCUPATION_CODE) //removed 'other' option to avoid duplication of default value
      .map(occupation => ({ label: occupation.jobTitle, value: occupation.code })) ?? [];

  const manualOccupation = occupations
    ? occupations.find(occupation => occupation.code === MANUAL_OCCUPATION_CODE)
    : null;

  //make sure defaultoption is available even if backend did not return default code 0
  const defaultOption: DefaultOptionType = manualOccupation
    ? { option: { label: manualOccupation.jobTitle, value: manualOccupation.code }, position: 'end' }
    : defaultManualOccupation;

  useEffect(() => {
    if (occupationState.occupation?.value && occupations?.length) {
      const code = occupationState.occupation.value;
      const jobTitle = occupationState.occupation.label; //use jobTitle label to set jobTitle field value as occupationData can have same code with different job titles
      const selectedOccupations = occupations.filter(occupation => occupation.code === code);
      const occupationMatchedFromOccupationList =
        selectedOccupations && selectedOccupations.find(occupation => occupation.jobTitle === jobTitle);
      setValue?.(codeFieldName, code as PathValue<TFieldValues, Path<TFieldValues>>);
      if (code !== MANUAL_OCCUPATION_CODE) {
        const shouldDisplayManualFieldIfFromOccupationMatching =
          !!applicantOccupationJobTitle && !occupationMatchedFromOccupationList;
        if (shouldDisplayManualFieldIfFromOccupationMatching) {
          setOccupationState({
            occupation: { label: otherOption.label, value: code },
            showManual: true,
          });
          // needed to display manual occupation on load
          setManualOccupationJobTitle?.(currValue => ({ ...currValue, isMatching: false, isFetched: false }));
        } else {
          setValue?.(jobTitleFieldName, jobTitle as PathValue<TFieldValues, Path<TFieldValues>>);
        }
      }
      // incomeThreshold is same for every occupation code
      setOccupationIncomeThreshold(selectedOccupations?.[0]?.incomeThreshold ?? 0);
    }
  }, [occupations]);

  // Hydrate occupationIncomeThreshold from a non-manual saved occupation code on revisit.
  // The existing effect at the top of this file depends on `occupationState`, which is local
  // state. This explicit effect on the watched code ensures the parent's threshold stays in
  // sync if the saved code arrives after mount or if the user changes the code via the combobox
  // (handleChange already sets it directly, but this is also safe to run for that path).
  useEffect(() => {
    if (applicantOccupationCode && applicantOccupationCode !== MANUAL_OCCUPATION_CODE && occupations?.length) {
      const matched = occupations.find(occ => occ.code === applicantOccupationCode);
      if (matched) {
        setOccupationIncomeThreshold(matched.incomeThreshold ?? 0);
      }
    }
  }, [applicantOccupationCode, occupations, setOccupationIncomeThreshold]);

  return (
    <Stack gap="xxlarge">
      <Field
        label={label}
        tone={showFieldError ? 'critical' : 'neutral'}
        message={showFieldError ? requiredFieldError(label) : ''}
        description={OCCUPATION_DESCRIPTION}
      >
        <Combobox
          placeholder="Start typing..."
          items={items}
          isLoading={isOccupationLoading}
          inputValue={inputValue || ''}
          onInputChange={handleInputChange}
          value={occupationState.occupation}
          onChange={handleChange}
          getOptionLabel={(option: OptionType) => option.label}
          getOptionValue={(option: OptionType) => option.value}
          defaultOption={!isOccupationLoading ? defaultOption : undefined}
        />
      </Field>
      {occupationState.showManual ? (
        <Stack gap="medium">
          <TextInputField
            name={jobTitleFieldName}
            label={MANUAL_OCCUPATION}
            control={control}
            onChange={e => {
              setManualOccupationJobTitle?.(currValue => ({ ...currValue, isMatched: false }));
            }}
            onBlur={e => {
              setManualOccupationJobTitle?.(currValue => ({
                ...currValue,
                jobTitle: e.target.value,
                isMatching: true,
              }));
            }}
          />
        </Stack>
      ) : (
        <input type="hidden" {...register?.(jobTitleFieldName)} />
      )}
    </Stack>
  );
}

export { OccupationField };
